import {
  CircularProgress,
  Stack,
  SvgIcon,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import {
  AppPrimaryButton,
  // AppSelectWithKeys,
} from 'shared/components/AppComponents';
import PageHeader from 'shared/components/PageHeader';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import ToolGovernanceList from './ToolGovernanceList';
import CreateToolDialog from './CreateToolDialog';
import { GuardrailsService } from 'services/GuardrailsService';
import { Toast, Utils } from 'shared/helpers';
import BuildIcon from '@mui/icons-material/Build';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

interface CreateToolPayload {
  name: string;
  serverId: string;
  approvalRequired: boolean;
}

export interface ToolItem {
  _id: string;
  name: string;
  serverId: string;
  approvalRequired: boolean;
  isActive: boolean;
  organizationId: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string | null;
  __v: number;
}

interface ServerItem {
  _id: string;
  name: string;
  description: string;
  endpoint: string;
  isActive: boolean;
  status: string;
  organizationId: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string | null;
}

const ToolGovernance = (): ReactElement => {
  const [selectedGovernance, setSelectedGovernance] = useState('');
  const [createToolDialog, setCreateToolDialog] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [serverList, setServerList] = useState<ServerItem[]>([]);
  const [toolsList, setToolsList] = useState<ToolItem[]>([]);
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  const fetchServerList = (): void => {
    setIsLoading(true);

    GuardrailsService.getserverList()
      .then((res) => {
        const { servers } = res.data.data;
        setServerList(servers || []);
      })
      .catch((err) => {
        Utils.handleErrorResponse(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchToolsList = (serverId: string): void => {
    setLoading(true);

    GuardrailsService.getAllTools(serverId)
      .then((res) => {
        const { tools } = res.data.data;

        setToolsList(tools || []);
      })
      .catch((err) => {
        Utils.handleErrorResponse(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const addTool = (values: any, formikHelper: any): void => {
    const payload: CreateToolPayload = {
      name: values.toolName,
      serverId: selectedGovernance,
      approvalRequired: values.isRequried,
    };

    GuardrailsService.createTool(payload)
      .then(() => {
        fetchToolsList(selectedGovernance);
        setCreateToolDialog(false);
        Toast.showSuccessMessage('Tool created successfully');
        formikHelper.resetForm();
      })
      .catch((err) => {
        Utils.handleErrorResponse(err);
      })
      .finally(() => {
        formikHelper.setSubmitting(false);
      });
  };

  const deleteTool = (id: string): void => {
    GuardrailsService.deleteTool(id)
      .then(() => {
        Toast.showSuccessMessage('Tool deleted successfully');
        fetchToolsList(selectedGovernance);
      })
      .catch((err) => {
        Utils.handleErrorResponse(err);
      });
  };

  useEffect(() => {
    fetchServerList();
  }, []);

  let content = null;

  if (isLoading) {
    content = (
      <Box className="empty-content">
        <CircularProgress />
      </Box>
    );
  } else if (selectedGovernance !== '') {
    content = (
      <ToolGovernanceList
        isLoading={loading}
        toolsList={toolsList}
        onDeleteTool={deleteTool}
      />
    );
  } else {
    content = (
      <Box className="app-plain-card empty-content mt-3">
        <SvgIcon
          component={BuildIcon}
          inheritViewBox
          sx={{ fontSize: 40 }}
          htmlColor="#3D3C42"
        />

        <Typography variant="body1" className="mt-2">
          Select a server to manage its tool allowlist.
        </Typography>
      </Box>
    );
  }
  return (
    <>
      <PageHeader
        noPadding
        title="Tool Governance"
        description="Manage which tools are allowed per server (default-deny)."
      />
      <Stack direction="row" spacing={3}>
        <Box sx={{ width: 276 }}>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={selectedGovernance}
              onChange={(e: any) => {
                const serverId = e.target.value;
                setSelectedGovernance(serverId);
                fetchToolsList(serverId);
              }}
            >
              <MenuItem disabled value="">
                <em>Select a Server</em>
              </MenuItem>
              {serverList.map((server) => (
                <MenuItem value={server._id}>{server.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box className="mt-1">
          {selectedGovernance !== '' &&
            orgACLConfig.policies?.includes(
              'policies.guardrails.toolgovernance.create'
            ) && (
              <AppPrimaryButton
                onClick={() => setCreateToolDialog(true)}
                startIcon={
                  <SvgIcon
                    component={Plus}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                  />
                }
                title="Add Tool"
              />
            )}
        </Box>
      </Stack>
      {content}
      <CreateToolDialog
        open={createToolDialog}
        onCloseCreateTool={() => setCreateToolDialog(false)}
        onSubmitTool={addTool}
      />
    </>
  );
};

export default ToolGovernance;
